import React, { useState } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  addLeadNote,
  useGetLeadNotes,
  getGetLeadNotesQueryKey
} from '../../../services/loanService';
import { RootState } from '../../../store/store';
import { formatDateTime } from '../../../utils/formatters';

/**
 * Notes kept against a lead while it is being worked.
 *
 * The team records what was discussed each time they speak to the prospective customer, so the
 * follow-up history stays with the lead instead of being lost when it passes between staff.
 * Notes are append-only - there is no edit or delete, because the value is the record of what
 * was said at the time.
 *
 * Rendered both in the leads table (inside a modal) and on the lead details page (as a tab), so
 * the same panel serves whichever way the user reaches it.
 */

const PAGE_SIZE = 50;

interface LeadNotesProps {
  leadId: string;
  /** Hides the panel's own heading where the surrounding chrome already names it. */
  showHeading?: boolean;
  className?: string;
}

const LeadNotes: React.FC<LeadNotesProps> = ({ leadId, showHeading = true, className = '' }) => {
  const [newNote, setNewNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();
  const user = useSelector((state: RootState) => state.auth.user);

  const { data, isLoading, isError } = useGetLeadNotes(
    leadId,
    { page: 0, size: PAGE_SIZE },
    { query: { enabled: !!leadId } }
  );

  const notes = data?.content || [];

  const handleAddNote = async (event: React.FormEvent) => {
    event.preventDefault();
    const note = newNote.trim();
    if (!leadId || !note) return;

    setIsSaving(true);
    try {
      await addLeadNote(leadId, {
        note,
        createdBy: user?.name || user?.username || undefined
      });
      setNewNote('');
      await queryClient.invalidateQueries({ queryKey: getGetLeadNotesQueryKey(leadId) });
      // The leads tables read the last note from the batch endpoint, so refresh that too or the
      // comment column keeps showing the previous note.
      await queryClient.invalidateQueries({ queryKey: ['/loan/api/v1/leads/notes/latest'], exact: false });
      toast.success('Note added');
    } catch (error) {
      console.error('Failed to add lead note:', error);
      toast.error('Could not add the note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={className}>
      {showHeading && (
        <div className="d-flex align-items-center mb-4">
          <div
            className="rounded-circle p-2 me-3"
            style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}
          >
            <i className="fa fa-sticky-note text-primary"></i>
          </div>
          <h5 className="mb-0 fw-bold">Comments</h5>
        </div>
      )}

      <Form onSubmit={handleAddNote} className="mb-4">
        <Form.Group controlId={`lead-note-text-${leadId}`}>
          <Form.Label className="small text-muted mb-1">Add a note</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={newNote}
            onChange={(event) => setNewNote(event.target.value)}
            placeholder="What was discussed with the customer?"
            maxLength={2000}
            disabled={isSaving}
          />
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">{newNote.length}/2000</small>
            <Button type="submit" variant="success" size="sm" disabled={isSaving || !newNote.trim()}>
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa fa-plus me-2"></i>
                  Add Note
                </>
              )}
            </Button>
          </div>
        </Form.Group>
      </Form>

      <h6 className="fw-bold mb-3">
        History
        {notes.length > 0 && (
          <span className="badge bg-light text-dark border ms-2">{notes.length}</span>
        )}
      </h6>

      {isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="success" />
          <p className="mt-2 mb-0 text-muted small">Loading notes...</p>
        </div>
      ) : isError ? (
        <Alert variant="warning" className="mb-0">
          <i className="fa fa-exclamation-triangle me-2"></i>
          Unable to load the notes for this lead.
        </Alert>
      ) : notes.length === 0 ? (
        <div className="text-center py-4">
          <i className="fa fa-sticky-note-o fa-2x text-muted mb-2 d-block"></i>
          <p className="text-muted mb-0">No notes yet. The first one goes above.</p>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {notes.map((note) => (
            <div key={note.id} className="border rounded p-3 bg-light bg-opacity-50">
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <span className="fw-semibold">
                  <i className="fa fa-user-circle me-2 text-success"></i>
                  {note.createdBy || 'Unknown user'}
                </span>
                <small className="text-muted">{formatDateTime(note.createdDate)}</small>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{note.note}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadNotes;
